/**
 * Turn-completion engine — the fix for "the agent stops mid-task".
 *
 * The model is bound to a completion contract (see system-prompt.ts): every
 * reply either calls a tool or ends with the literal marker {@link DONE_MARKER}
 * after a final summary. This module enforces the contract from the outside:
 *
 *  - {@link createDoneFilter} strips the marker from the visible stream (the
 *    user never sees it) while recording that it appeared;
 *  - {@link decideNext} is the pure policy: given how a model call ended
 *    (marker? length-cut? unverified writes? failing checks?) it picks the next
 *    action — auto-continue, auto-verify, repair, or accept — all BOUNDED so a
 *    confused model can never loop forever;
 *  - {@link runVerifyPhase} runs the repo's own checks (typecheck/lint/test)
 *    after the agent wrote files, so "done" means "the goal is reached and the
 *    project still passes its checks", not "the model said so".
 */
import type { ChatTurn, FutureXClient, Message, UsageReport } from '@fim/futurex-protocol';
import { classifyVerifyNeed, type ChangeSet } from './change-significance.js';
import { detectChecks, runDiagnosticsAsync, type AsyncCheckResult } from './diagnostics.js';
import { DONE_MARKER } from './marker.js';
import { accumulateUsage } from './pricing.js';
import { renderStream } from './render.js';
import { summarizeFailures } from './repair.js';
import { info, line, phase, startSpinner, verdictLine } from './ui.js';

export { createDoneFilter, DONE_MARKER, NOVERIFY_MARKER, type DoneFilter } from './marker.js';

/** Bounded continuation budgets — a confused model can never loop forever. */
export const MAX_LENGTH_CONTINUES = 3;
export const MAX_FINISH_NUDGES = 2;
export const MAX_REPAIR_ROUNDS = 3;

/** Auto-continue prompt when the model hit the output-token ceiling mid-work. */
export const NUDGE_LENGTH = [
  'Your previous message hit the output-length limit mid-work.',
  'Continue EXACTLY where you stopped — do not repeat anything already produced.',
  `Finish the remaining steps; end with the summary + ${DONE_MARKER} only when fully done.`,
].join(' ');

/** Nudge when the model stopped without a tool call and without the marker. */
export const NUDGE_FINISH = [
  'Your last reply ended without a tool call and without the completion marker.',
  `If the request is fully complete and verified, reply with ONLY the marker ${DONE_MARKER}`,
  '(nothing else — it is hidden from the user). Otherwise continue the remaining work NOW',
  'using your tools; never stop to narrate what you plan to do.',
].join(' ');

/** Feed verification failures back as the next instruction (repair round). */
export function nudgeVerifyFailures(failureSummary: string): string {
  return [
    'Automatic verification ran the project checks and found failures:',
    '',
    failureSummary,
    '',
    'Fix ALL of these now: read the failing files, correct them with write_file,',
    `and only when everything passes finish with your summary + ${DONE_MARKER}.`,
  ].join('\n');
}

// --- the completion policy ----------------------------------------------------

/** How verification is applied to a turn. */
export type VerifyMode = 'auto' | 'off';

/** Everything the policy needs to know about how the last model call ended. */
export interface TurnState {
  /** The completion marker appeared in this call's output. */
  readonly sawMarker: boolean;
  /** Provider finish reason of the last call ('length' = cut off mid-output). */
  readonly finishReason?: string | undefined;
  /** A mutating tool ran since the last verification (or ever, if none ran). */
  readonly mutatedSinceVerify: boolean;
  /** Verification ran for the current state of the tree. */
  readonly verifyRan: boolean;
  /** Verification ran and produced real failures (not timeouts). */
  readonly verifyFailed: boolean;
  /**
   * The pending mutations actually warrant the project checks. False when the
   * change is too small/safe to matter (docs only, or an agent-waved tiny edit
   * inside the blast-radius guard — see change-significance.ts). Only consulted
   * when there are unverified mutations.
   */
  readonly changesNeedVerify: boolean;
  readonly lengthContinues: number;
  readonly finishNudges: number;
  readonly repairRounds: number;
  readonly verifyMode: VerifyMode;
}

export type NextAction =
  /** The turn is complete — accept the last text as the final answer. */
  | { readonly kind: 'accept' }
  /** Output was cut at the token ceiling — auto-continue. */
  | { readonly kind: 'continue-length' }
  /** Files changed and checks haven't run — run verification now. */
  | { readonly kind: 'verify' }
  /** Verification failed — feed the failures back for a repair round. */
  | { readonly kind: 'repair' }
  /** No tool call, no marker — demand completion or continuation. */
  | { readonly kind: 'nudge-finish' };

/**
 * The pure completion policy, applied whenever a model call ends WITHOUT tool
 * calls. Order matters: a length cut is continued first; unverified writes that
 * actually need it are verified before anything is accepted; real failures get
 * bounded repair rounds; a missing marker gets a bounded "finish or continue"
 * nudge; and a verified clean tree is accepted even without the marker
 * (verification is the stronger signal — no extra model call just for etiquette).
 *
 * `changesNeedVerify` lets a trivial change (docs only, or an agent-waved tiny
 * edit) bypass the verify phase — the change is too small/safe for any check to
 * catch, so it falls straight through to the accept/nudge logic.
 */
export function decideNext(s: TurnState): NextAction {
  if (s.finishReason === 'length' && s.lengthContinues < MAX_LENGTH_CONTINUES) {
    return { kind: 'continue-length' };
  }
  if (s.verifyMode === 'auto' && s.mutatedSinceVerify && !s.verifyRan && s.changesNeedVerify) {
    return { kind: 'verify' };
  }
  if (s.verifyRan && s.verifyFailed && s.repairRounds < MAX_REPAIR_ROUNDS) {
    return { kind: 'repair' };
  }
  const verifiedClean = s.verifyRan && !s.verifyFailed;
  if (!s.sawMarker && !verifiedClean && s.finishNudges < MAX_FINISH_NUDGES) {
    return { kind: 'nudge-finish' };
  }
  return { kind: 'accept' };
}

// --- the verification phase ----------------------------------------------------

export interface VerifyOutcome {
  /** True when no check failed (timeouts don't count as failures). */
  readonly ok: boolean;
  /** False when the repo has no runnable checks (nothing was verified). */
  readonly ran: boolean;
  /** One rendered line per check (for the UI). */
  readonly lines: readonly string[];
  /** Compact failure context to feed back to the model. */
  readonly failureSummary: string;
  /** At least one check was killed at the time budget (inconclusive, not failed). */
  readonly timedOut: boolean;
  readonly results: readonly AsyncCheckResult[];
}

/** Per-check time budget. A timeout is reported, not repaired. */
const VERIFY_CHECK_TIMEOUT_MS = 240_000;

/**
 * Run the repo's own checks (typecheck → lint → test, fail-fast; `build` is
 * skipped — slow and usually covered by typecheck) and shape the outcome for
 * both the UI and the repair nudge.
 */
export async function runVerifyPhase(
  root: string,
  opts: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<VerifyOutcome> {
  const checks = detectChecks(root).filter((c) => c.name !== 'build');
  if (checks.length === 0) {
    return { ok: true, ran: false, lines: [], failureSummary: '', timedOut: false, results: [] };
  }
  const results = await runDiagnosticsAsync(root, {
    checks,
    failFast: true,
    timeoutMs: opts.timeoutMs ?? VERIFY_CHECK_TIMEOUT_MS,
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  const failures = results.filter((r) => !r.ok && !r.timedOut);
  const timedOut = results.some((r) => r.timedOut === true);
  const lines = results.map((r) => {
    const secs = (r.durationMs / 1000).toFixed(1);
    const state = r.timedOut ? 'timed out' : r.ok ? 'pass' : 'FAIL';
    return `${r.name} ${state} (${secs}s)`;
  });
  return {
    ok: failures.length === 0,
    ran: true,
    lines,
    failureSummary: failures.length > 0 ? summarizeFailures(failures) : '',
    timedOut,
    results,
  };
}

// --- gateway-mode completion loop ----------------------------------------------

/**
 * Hard outer bound on client-driven model rounds per user turn (the gateway
 * itself loops tools within each round). Covers the worst legal case:
 * 1 + length-continues + finish-nudges + repair-rounds, with slack.
 */
const MAX_GATEWAY_ROUNDS = 12;

export interface GatewayTurnDeps {
  readonly client: FutureXClient;
  /** Shared conversation history — nudges and replies are appended here. */
  readonly history: Message[];
  /** Routing fields re-sent on every round (model/tier/effort/maxTokens). */
  readonly base: Omit<ChatTurn, 'messages' | 'signal'>;
  readonly signal: AbortSignal;
  /** Repo root, for the automatic verification phase. */
  readonly root: string;
  readonly verify: VerifyMode;
  /** True when a mutating tool ran since {@link resetMutated} (client-side). */
  mutated(): boolean;
  /** The accumulated file changes since {@link resetMutated}, for significance. */
  changes(): ChangeSet;
  /** Clear the mutation flag AND the accumulated changes (after verification). */
  resetMutated(): void;
}

/**
 * Drive a gateway-backed turn to ACTUAL completion. The gateway streams one
 * agentic round; this loop applies the same completion policy as the local
 * agent — auto-continue on a length cut, verify after writes, bounded repair
 * rounds on failures, and a finish-or-continue nudge when the model stops
 * without the completion marker. Used by the REPL runtime and one-shot
 * `futurex agent` so both finish without the user typing "continue".
 */
export async function runGatewayTurnToCompletion(
  deps: GatewayTurnDeps,
): Promise<{ usage?: UsageReport; error?: string }> {
  let usage: UsageReport | undefined;
  let verifyMode = deps.verify;
  let lengthContinues = 0;
  let finishNudges = 0;
  let repairRounds = 0;
  let verifyRan = false;
  let verifyFailed = false;
  // Sticky: once a check has failed this turn, every later change is re-verified
  // (the model's trivial waiver can't be trusted once the build is known broken).
  let everFailedVerify = false;
  let shownPhase: string | null = null;
  let lastVerify: VerifyOutcome | null = null;
  deps.resetMutated();

  const showPhase = (p: string): void => {
    if (shownPhase !== p) {
      shownPhase = p;
      phase(p);
    }
  };

  for (let round = 0; round < MAX_GATEWAY_ROUNDS; round++) {
    const result = await renderStream(deps.client, {
      ...deps.base,
      messages: deps.history,
      // Round 0 carries the human's actual prompt; later rounds replay synthetic
      // nudges, which must NOT be audit-logged as user prompts by the gateway.
      newTurn: round === 0,
      signal: deps.signal,
    });
    if (result.usage) usage = usage ? accumulateUsage(usage, result.usage) : result.usage;
    if (result.error) return { ...(usage ? { usage } : {}), error: result.error };
    if (result.text) deps.history.push({ role: 'assistant', content: result.text });
    if (deps.signal.aborted) return usage ? { usage } : {};
    if (deps.mutated()) {
      verifyRan = false;
      verifyFailed = false;
    }

    // Does this turn's pending change actually warrant the project checks?
    let changesNeedVerify = true;
    if (verifyMode === 'auto' && deps.mutated() && !verifyRan) {
      if (everFailedVerify) {
        // A check already failed this turn — re-verify regardless of the waiver.
        changesNeedVerify = true;
      } else {
        const need = classifyVerifyNeed(deps.changes(), { modelWaived: result.sawNoVerify === true });
        changesNeedVerify = need.verify;
        if (!need.verify) info(`skipping verification — ${need.reason}`);
      }
    }

    let proceed = false;
    while (!proceed) {
      const action = decideNext({
        sawMarker: result.sawMarker === true,
        finishReason: result.finishReason,
        mutatedSinceVerify: deps.mutated(),
        verifyRan,
        verifyFailed,
        changesNeedVerify,
        lengthContinues,
        finishNudges,
        repairRounds,
        verifyMode,
      });
      switch (action.kind) {
        case 'accept': {
          if (verifyFailed && lastVerify) {
            for (const l of lastVerify.lines) {
              if (l.includes('FAIL')) line(verdictLine(false, l));
            }
            info('verification failures remain — say "fix the failing checks" to retry');
          }
          return usage ? { usage } : {};
        }
        case 'continue-length':
          lengthContinues += 1;
          info('output hit the token limit — continuing automatically');
          deps.history.push({ role: 'user', content: NUDGE_LENGTH });
          proceed = true;
          break;
        case 'nudge-finish':
          finishNudges += 1;
          deps.history.push({ role: 'user', content: NUDGE_FINISH });
          proceed = true;
          break;
        case 'repair':
          showPhase('repair');
          repairRounds += 1;
          info(`repair round ${repairRounds} — feeding the failures back`);
          deps.history.push({
            role: 'user',
            content: nudgeVerifyFailures(lastVerify?.failureSummary ?? 'checks failed'),
          });
          proceed = true;
          break;
        case 'verify': {
          showPhase('verify');
          const spin = startSpinner('verifying — running project checks…');
          try {
            lastVerify = await runVerifyPhase(deps.root, { signal: deps.signal });
          } finally {
            spin.stop();
          }
          if (deps.signal.aborted) return usage ? { usage } : {};
          if (!lastVerify.ran) {
            info('no typecheck/lint/test scripts detected — skipping verification');
            verifyMode = 'off';
            verifyRan = false;
            verifyFailed = false;
          } else {
            deps.resetMutated();
            verifyRan = true;
            verifyFailed = !lastVerify.ok;
            if (verifyFailed) everFailedVerify = true;
            for (const l of lastVerify.lines) line(verdictLine(!l.includes('FAIL'), l));
            if (lastVerify.timedOut) info('a check hit its time budget — result treated as inconclusive');
          }
          break; // re-decide with the verification outcome
        }
      }
    }
  }
  info('reached the round safety limit — stopping; send another message to continue');
  return usage ? { usage } : {};
}
